/**
 * Origin Rules — per-domain custom proxy-pass rules for the Multi-CDN tab. Pick a
 * domain, then review its rules (ordered by priority) and toggle each one on/off,
 * edit, or delete it.
 *
 * Backed by `/waf/origin/*` (Owl/Mlytics, customer-scoped). The schema-driven
 * rule builder (create/edit) lives in `WafOriginRuleForm`.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Server, RotateCcw, Trash2, Pencil, Plus } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Badge,
  Skeleton,
  Button,
  IconButton,
  Switch,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Tooltip,
  TooltipProvider,
} from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useWafOriginRules,
  useSetOriginRuleEnabled,
  useDeleteOriginRule,
  fmtEpoch,
  wafExtractError,
  type OriginRule,
  type OriginRuleAction,
  type OriginRuleCondition,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import { WafScopeBar, type WafScope } from '../analytics/WafScopeBar';
import styles from './WafOriginRules.module.css';

const muted = (v: React.ReactNode) => <span style={{ color: 'var(--vn-fg-muted)' }}>{v}</span>;
const mono = { fontFamily: 'var(--vn-font-mono)' } as const;

/** Build the create/edit form URL (+ optional rule id). */
function formHref(serviceId: string, domain: string, id?: string) {
  const q = new URLSearchParams({ domain });
  if (id) q.set('id', id);
  return `/services/waf/${serviceId}/origin/${id ? 'edit' : 'new'}?${q.toString()}`;
}

/** A short, human summary of the proxy-pass target (sources + ports). */
function actionDetail(a: OriginRuleAction): string {
  const sources = a.sources?.length ? a.sources.join(', ') : '';
  const ports = [a.port_http && `:${a.port_http}`, a.port_https && `:${a.port_https} (TLS)`].filter(Boolean).join(' ');
  return [sources, ports].filter(Boolean).join('  ');
}

/** Compact one-line rendering of a condition. */
function condText(c: OriginRuleCondition): string {
  const val = c.values?.length ? c.values.join(', ') : (c.value ?? '');
  const field = c.key ? `${c.field}[${c.key}]` : c.field;
  return [field, c.operator, val].filter(Boolean).join(' ');
}

/** First couple conditions as chips + a `+N` overflow. */
function conditionsCell(conds: OriginRuleCondition[] | undefined, max = 2) {
  const items = conds ?? [];
  if (items.length === 0) return muted('—');
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...mono, fontSize: 'var(--vn-font-size-sm)' }}>
      {items.slice(0, max).map(condText).join('  •  ')}
      {items.length > max ? muted(` +${items.length - max}`) : null}
    </span>
  );
}

/** Inline enable/disable toggle for a rule. */
function EnableCell({ serviceId, domain, rule }: { serviceId: string; domain: string; rule: OriginRule }) {
  const { show } = useToast();
  const setEnabled = useSetOriginRuleEnabled(serviceId, {
    onSuccess: (_d, vars) => { show({ tone: 'success', title: vars.enable ? 'Rule enabled' : 'Rule disabled' }); trackFeatureAction('waf', 'origin_rule', vars.enable ? 'enable' : 'disable'); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });
  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
      <Switch
        checked={rule.enable}
        disabled={setEnabled.isPending}
        aria-label={`Enable ${rule.name}`}
        onCheckedChange={(enable) => setEnabled.mutate({ domain, id: rule.id, enable })}
      />
    </div>
  );
}

/** Edit + delete actions for a rule. */
function ActionsCell({ serviceId, domain, rule }: { serviceId: string; domain: string; rule: OriginRule }) {
  const { show } = useToast();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const del = useDeleteOriginRule(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Rule deleted' }); trackFeatureAction('waf', 'origin_rule', 'delete'); setConfirmDelete(false); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Delete failed') }),
  });
  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', gap: 2 }}>
      <TooltipProvider>
        <Tooltip content="Edit" side="top">
          <IconButton variant="ghost" size="sm" aria-label="Edit rule" onClick={() => navigate(formHref(serviceId, domain, rule.id))}>
            <Pencil size={14} />
          </IconButton>
        </Tooltip>
        <Tooltip content="Delete" side="top">
          <IconButton variant="ghost" size="sm" aria-label="Delete rule" onClick={() => setConfirmDelete(true)}>
            <Trash2 size={14} />
          </IconButton>
        </Tooltip>
      </TooltipProvider>
      {confirmDelete && (
        <ConfirmDialog
          open
          onOpenChange={(o) => { if (!o) setConfirmDelete(false); }}
          title="Delete rule?"
          description={`Delete the rule "${rule.name}"? This cannot be undone.`}
          confirmLabel="Delete"
          destructive
          loading={del.isPending}
          onConfirm={() => del.mutate({ domain, id: rule.id })}
        />
      )}
    </div>
  );
}

/** Origin rules table for a domain. */
function RulesCard({ serviceId, domain }: { serviceId: string; domain: string }) {
  const navigate = useNavigate();
  const goAdd = () => navigate(formHref(serviceId, domain));
  const { data, isLoading, isError, refetch } = useWafOriginRules(serviceId, domain);
  const rules = data ?? [];

  const columns = useMemo<ColumnDef<OriginRule, unknown>[]>(
    () => [
      {
        id: 'priority',
        header: '#',
        enableSorting: false,
        cell: ({ row }) => muted(row.original.priority ?? '—'),
      },
      {
        id: 'name',
        header: 'Name',
        enableSorting: false,
        cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.name}</span>,
      },
      {
        id: 'conditions',
        header: 'Conditions',
        enableSorting: false,
        cell: ({ row }) => conditionsCell(row.original.conditions),
      },
      {
        id: 'origin',
        header: 'Origin',
        enableSorting: false,
        cell: ({ row }) => {
          const a = row.original.action;
          const detail = actionDetail(a);
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Badge tone="neutral" appearance="subtle" size="sm">{a.origin_type || 'Proxy'}</Badge>
              {detail ? <span style={{ ...mono, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{detail}</span> : null}
            </span>
          );
        },
      },
      {
        id: 'enable',
        header: 'Enabled',
        enableSorting: false,
        cell: ({ row }) => <EnableCell serviceId={serviceId} domain={domain} rule={row.original} />,
      },
      {
        id: 'updated',
        header: 'Updated',
        enableSorting: false,
        cell: ({ row }) => muted(fmtEpoch(row.original.updated_at)),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => <ActionsCell serviceId={serviceId} domain={domain} rule={row.original} />,
      },
    ],
    [serviceId, domain],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Origin rules{rules.length ? ` (${rules.length})` : ''}</CardTitle>
      </CardHeader>
      <CardBody>
        <div className={styles.toolbar}>
          <div className={styles.spacer} />
          <div className={styles.actions}>
            <Button variant="secondary" startIcon={<RotateCcw size={15} />} onClick={() => refetch()} aria-label="Refresh rules">
              Refresh
            </Button>
            <Button startIcon={<Plus size={15} />} onClick={goAdd}>Add rule</Button>
          </div>
        </div>

        {isLoading ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} style={{ height: 44 }} />)}
          </div>
        ) : isError ? (
          <Alert tone="danger" title="Couldn't load origin rules" actions={<button onClick={() => refetch()}>Retry</button>}>
            The WAF service couldn't return this domain's origin rules. Try again.
          </Alert>
        ) : rules.length === 0 ? (
          <EmptyState
            icon={<Server size={20} />}
            title="No origin rules yet"
            description="This domain has no custom origin rules. Create one to route matched requests to a specific origin."
            actions={<Button startIcon={<Plus size={15} />} onClick={goAdd}>Add rule</Button>}
          />
        ) : (
          <DataTable columns={columns} data={rules} getRowId={(r) => r.id} emptyState="No origin rules." />
        )}
      </CardBody>
    </Card>
  );
}

export function WafOriginRules({ serviceId }: { serviceId: string }) {
  const [scope, setScope] = useState<WafScope>({});
  const domain = scope.subdomain;
  const noSubdomain = !!scope.zone && !domain;

  return (
    <Container size="xl">
      <PageHeader
        icon={<Server size={22} />}
        title="Origin Rules"
        description="Route matched requests to a specific origin per subdomain (proxy-pass overrides)."
      />

      <div className={styles.scopeBar}>
        <WafScopeBar serviceId={serviceId} mode="subdomain" onChange={setScope} />
      </div>

      {noSubdomain ? (
        <EmptyState
          icon={<Server size={20} />}
          title="No proxied subdomains"
          description="This zone has no proxied subdomain to manage origin rules for. Proxy a record first."
        />
      ) : !domain ? (
        <EmptyState
          icon={<Server size={20} />}
          title="Select a subdomain"
          description="Pick a domain and subdomain above to manage its origin rules."
        />
      ) : (
        <RulesCard serviceId={serviceId} domain={domain} />
      )}
    </Container>
  );
}

export default WafOriginRules;
